import React from 'react';

import { Icon, Portal } from '@sravni/react-design-system';
import { Fade } from '@sravni/react-design-system/lib/Transition';
import { Share } from '@sravni/react-icons';
import { useBoolean, usePositioner, useStyles } from '@sravni/react-utils';

import { Modifier } from '@popperjs/core';

import { IAnalyticsObject } from '../../helpers/analyticsEvents';

import { DynamicShareButtons } from './components/ShareButtons';
import styles from './share.module.scss';

interface IProps {
  shareUrl: string;
  title: string;
  analyticLabel: IAnalyticsObject;
}

const DesktopShare: React.FC<IProps> = ({ shareUrl, title, analyticLabel }) => {
  const cx = useStyles(styles);
  const reference = React.useRef<Element | null>(null);
  const popper = React.useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useBoolean(false);

  const minWidthModifier: Modifier<'minWidth', any> = {
    name: 'minWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
      state.styles.popper.minWidth = '280px';
    },
  };
  const { referenceRef, popperRef, getReferenceProps, getPopperProps } = usePositioner({
    placement: 'bottom-start',
    modifiers: [minWidthModifier],
  });

  React.useEffect(() => {
    // @ts-ignore
    let timer = null;
    const listener = (event: Event) => {
      // @ts-ignore
      if (reference.current && reference.current.contains(event.target)) {
        return;
      }

      // @ts-ignore
      if (popper.current && popper.current.contains(event.target)) {
        timer = setTimeout(() => {
          setVisible.off();
        }, 200);
        return;
      }

      setVisible.off();
    };

    document.addEventListener('mousedown', listener);

    return () => {
      // @ts-ignore
      timer && clearTimeout(timer);
      document.removeEventListener('mousedown', listener);
    };
  }, [reference, popper, setVisible]);

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    setVisible.toggle();
  };

  return (
    <div className={styles.wrapper}>
      <div
        ref={referenceRef}
        tabIndex={0}
        className={cx('shareLink', { active: visible })}
        onClick={handleClick}
        {...getReferenceProps({}, reference)}
      >
        <Icon size={20} icon={<Share />} />
      </div>
      <Portal>
        <Fade
          ref={popperRef}
          className={cx('bubble')}
          visible={visible}
          unmountOnExit
          {...getPopperProps({ style: { zIndex: 1060 } }, popper)}
        >
          <div className={styles.dropdownContent}>
            <div className={styles.dropdownMenu}>
              <DynamicShareButtons shareUrl={shareUrl} title={title} analyticLabel={analyticLabel} />
            </div>
          </div>
        </Fade>
      </Portal>
    </div>
  );
};

export default DesktopShare;
